<template>
    <div v-if="context && context.isEnrolled" class="medsync-banner">
        <b-card :class="['mb-3', bannerClass]" no-body>
            <b-card-body class="py-2 px-3">
                <!-- Full View -->
                <div v-if="!minimized">
                    <b-row align-v="center">
                        <b-col cols="auto">
                            <i class="fa fa-sync-alt fa-lg text-primary"></i>
                        </b-col>
                        <b-col>
                            <div class="d-flex justify-content-between align-items-center">
                                <div>
                                    <h6 class="mb-1 font-weight-bold text-primary">
                                        <i class="fa fa-sync-alt"></i> MedSync Patient
                                    </h6>
                                    <div class="medsync-info">
                                        <span class="mr-3">
                                            <strong>Next Anchor Date:</strong>
                                            <span :class="anchorDateClass">
                                                {{ formatAnchorDate }}
                                                <span v-if="context.daysUntilAnchor !== null" class="days-until">
                                                    ({{ daysUntilText }})
                                                </span>
                                            </span>
                                        </span>
                                        <span class="mr-3">
                                            <strong>Cycle:</strong> {{ context.cycleDays }} days
                                        </span>
                                        <span>
                                            <strong>Medications in Sync:</strong>
                                            <a href="#" @click.prevent="viewDetails" class="medication-count">
                                                {{ context.medicationCount }}
                                            </a>
                                        </span>
                                    </div>

                                    <!-- Eligibility Indicator -->
                                    <div v-if="showEligibilityIndicator" class="mt-2">
                                        <span v-if="context.isMedicationEligible && !context.isPrescriptionInMedSync" class="text-success">
                                            <i class="fa fa-check-circle"></i>
                                            This medication is eligible to be added to MedSync
                                        </span>
                                        <span v-else-if="context.isPrescriptionInMedSync" class="text-info">
                                            <i class="fa fa-info-circle"></i>
                                            This medication is already in the MedSync program
                                        </span>
                                        <span v-else-if="!context.isMedicationEligible && context.ineligibilityReason" class="text-muted">
                                            <i class="fa fa-info-circle"></i>
                                            {{ context.ineligibilityReason }}
                                        </span>
                                    </div>
                                </div>

                                <!-- Action Buttons -->
                                <div class="d-flex align-items-center">
                                    <b-button
                                        variant="outline-primary"
                                        size="sm"
                                        class="mr-2"
                                        @click="viewDetails">
                                        <i class="fa fa-list"></i> View Details
                                    </b-button>
                                    <b-button
                                        v-if="canAddToMedSync"
                                        variant="primary"
                                        size="sm"
                                        class="mr-2"
                                        @click="addToMedSync">
                                        <i class="fa fa-plus"></i> Add to MedSync
                                    </b-button>
                                    <b-button
                                        variant="link"
                                        size="sm"
                                        class="text-muted p-0"
                                        @click="minimized = true">
                                        <i class="fa fa-minus"></i>
                                    </b-button>
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                </div>

                <!-- Minimized View -->
                <div v-else class="d-flex justify-content-between align-items-center">
                    <span class="text-primary">
                        <i class="fa fa-sync-alt"></i>
                        <strong>MedSync</strong> | Next: {{ formatAnchorDateShort }}
                        <span v-if="context.daysUntilAnchor !== null" :class="anchorDateClass">
                            ({{ context.daysUntilAnchor }}d)
                        </span>
                    </span>
                    <b-button
                        variant="link"
                        size="sm"
                        class="text-primary p-0"
                        @click="minimized = false">
                        <i class="fa fa-plus"></i> Expand
                    </b-button>
                </div>
            </b-card-body>
        </b-card>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { MedSyncPrescriptionContext } from '@/models/MedSync';
import axios from 'axios';
import moment from 'moment';

@Component
export default class MedSyncBanner extends Vue {
    @Prop({ required: true }) patientId!: number;
    @Prop({ default: null }) rxId!: string | null;
    @Prop({ default: false }) sticky!: boolean;

    private context: MedSyncPrescriptionContext | null = null;
    private loading = false;
    private minimized = false;

    async mounted() {
        await this.loadContext();
    }

    @Watch('patientId')
    async onPatientIdChanged() {
        await this.loadContext();
    }

    @Watch('rxId')
    async onRxIdChanged() {
        await this.loadContext();
    }

    async loadContext() {
        if (!this.patientId) return;

        this.loading = true;
        try {
            const params = this.rxId ? { rxId: this.rxId } : {};
            const response = await axios.get<MedSyncPrescriptionContext>(
                `/api/medsync/prescription-context/${this.patientId}`,
                { params }
            );
            this.context = response.data;
        } catch (error) {
            console.error('Error loading MedSync context:', error);
            this.context = null;
        } finally {
            this.loading = false;
        }
    }

    get bannerClass(): string {
        if (!this.context?.daysUntilAnchor) return 'border-primary';

        const days = this.context.daysUntilAnchor;
        if (days <= 6) return 'border-danger bg-light-danger';
        if (days <= 14) return 'border-warning bg-light-warning';
        return 'border-success bg-light-success';
    }

    get anchorDateClass(): string {
        if (!this.context?.daysUntilAnchor) return '';

        const days = this.context.daysUntilAnchor;
        if (days <= 6) return 'text-danger font-weight-bold';
        if (days <= 14) return 'text-warning font-weight-bold';
        return 'text-success font-weight-bold';
    }

    get formatAnchorDate(): string {
        if (!this.context?.nextAnchorDate) return 'N/A';
        return moment(this.context.nextAnchorDate).format('dddd, MMMM D, YYYY');
    }

    get formatAnchorDateShort(): string {
        if (!this.context?.nextAnchorDate) return 'N/A';
        return moment(this.context.nextAnchorDate).format('MMM D, YYYY');
    }

    get daysUntilText(): string {
        if (this.context?.daysUntilAnchor === null) return '';
        const days = this.context!.daysUntilAnchor!;

        if (days === 0) return 'today';
        if (days === 1) return 'tomorrow';
        return `in ${days} days`;
    }

    get showEligibilityIndicator(): boolean {
        return !!this.rxId && this.context !== null;
    }

    get canAddToMedSync(): boolean {
        return !!(
            this.context?.isMedicationEligible &&
            !this.context?.isPrescriptionInMedSync &&
            this.rxId
        );
    }

    viewDetails() {
        this.$emit('view-details', this.context);
    }

    addToMedSync() {
        this.$emit('add-to-medsync', {
            enrollmentId: this.context?.enrollmentId,
            rxId: this.rxId,
        });
    }

    public refresh() {
        return this.loadContext();
    }
}
</script>

<style scoped>
.medsync-banner {
    position: relative;
}

.medsync-info {
    font-size: 0.9rem;
    color: #495057;
}

.days-until {
    font-weight: 600;
}

.medication-count {
    color: #007bff;
    text-decoration: underline;
    cursor: pointer;
}

.medication-count:hover {
    color: #0056b3;
}

.bg-light-success {
    background-color: #d4edda !important;
}

.bg-light-warning {
    background-color: #fff3cd !important;
}

.bg-light-danger {
    background-color: #f8d7da !important;
}

.border-success {
    border-left: 4px solid #28a745 !important;
}

.border-warning {
    border-left: 4px solid #ffc107 !important;
}

.border-danger {
    border-left: 4px solid #dc3545 !important;
}

.border-primary {
    border-left: 4px solid #007bff !important;
}
</style>
